import { useForm } from "@refinedev/react-hook-form";
import { TextFieldElement } from 'react-hook-form-mui';
import {
    Typography,
    Stack,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateOrEdit } from '@/components/editcreate';
import ProductSelect from '@/components/form/ProductSelect';
import { useList, useParsed } from '@refinedev/core';
import { DealRuleForm } from '@/components/form/DealRuleForm';
import { useMemo } from 'react';
import { IDealForm, DealFormSchema } from '../../utils/zod/dealFormValidation';
import { ICollection, IProduct } from '@/utils/zod/productCollectionFormValidation';
import { defaultRule } from "@/utils/helpers/ruleHelper";

export const CreateEditDeal = ({ actionType }: { actionType: 'create' | 'edit' }) => {

    const { id } = useParsed();
    // Create form context at the parent level
    const formContext = useForm<IDealForm>({
        resolver: zodResolver(DealFormSchema),
        mode: 'onChange',
        refineCoreProps: {
            action: actionType,
            resource: "deals",
            id: actionType === 'edit' ? id : undefined,
            redirect: "list",
            queryOptions: {
                select: useMemo(() => {
                    return (data: any) => {
                        if (!data || !data.data || !data.data.tag_rules) return data;
                        
                        return {
                            ...data,
                            data: {
                                ...data.data,
                                tag_rules: data.data.tag_rules.map((rule: any) => ({
                                    ...rule,
                                    discount: {
                                        ...rule.discount,
                                        product_creation_based_tiers: rule.discount.product_creation_based_tiers.map((tier: any) => ({
                                            ...tier,
                                            start_date: tier.start_date ? new Date(tier.start_date) : tier.start_date,
                                            end_date: tier.end_date ? new Date(tier.end_date) : tier.end_date
                                        }))
                                    }
                                }))
                            }
                        };
                    };
                }, []), // Empty dependency array means this function is created only once
            },
        },
        defaultValues: actionType === 'create' ? {
            name: '',
            description: '',
            products: [],
            collections: [],
            tag_rules: [defaultRule]
        } : undefined
    });

    const { data: products } = useList<IProduct>({
        resource: "products",
    });

    // Memoize the mapped products array
    const mappedProducts = useMemo(() =>
        products?.data?.map((product: IProduct) => ({
            label: product.name,
            id: product.id,
            image: product.medias[0].cdn_url,
        })) ?? [],
        [products?.data]
    );

    const { data: collections } = useList<ICollection>({
        resource: "collections",
    });

    // Memoize the mapped collections array
    const mappedCollections = useMemo(() =>
        collections?.data?.map((collection: ICollection) => ({
            label: collection.name,
            id: collection.id,
        })) ?? [],
        [collections?.data]
    );

    return (
        <CreateOrEdit formValues={formContext} actionType={actionType} title={{edit: `Edit Deal #${formContext.getValues("name")}`}} >
            <Stack spacing={2} sx={{ mb: 2, p: 2 }}>
                <TextFieldElement 
                    name="name" 
                    label="Deal Name" 
                    fullWidth 
                    required 
                    autoComplete="no-name" 
                    slotProps={{
                        inputLabel: {
                            sx: {
                                '& .MuiFormLabel-asterisk': {
                                    color: 'error.main',
                                    fontSize: '24px',
                                },
                            },
                        },
                    }}
                    size="small"
                    autoFocus
                />
                <TextFieldElement name="description" label="Deal Description" fullWidth size="small" />
                <DealRuleForm
                    name="tag_rules"
                />
                <Typography variant="h6">Products</Typography>
                <ProductSelect
                    type="product"
                    options={mappedProducts}
                    name="products"
                />
                <ProductSelect
                    type="collection"
                    options={mappedCollections}
                    name="collections"
                />
            </Stack>
        </CreateOrEdit>
    );
};